import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import PhotoCapture from './PhotoCapture';
import TrustBadge from './TrustBadge';
import { sendElosEvent } from '../utils/elosEvents';

const ReceiverView = ({ data, save, user, addNotification }) => {
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({});
  const [photos, setPhotos] = useState({});

  const complete = () => {
    if (!photos.received || !form.qty || !form.cond) {
      alert('Complete all required fields');
      return;
    }
    const dispatch = data.dispatches.find(d => d.id === sel);
    const mismatch = parseInt(form.qty) !== dispatch.quantity;
    const damaged = form.cond === 'damaged';

    if (mismatch || damaged) {
      if (!photos.damage) {
        alert('Damage/discrepancy photo required');
        return;
      }
      const incident = {
        id: `INC-${Date.now()}`,
        dispatchId: dispatch.id,
        type: damaged ? 'damage' : 'mismatch',
        quantityExpected: dispatch.quantity,
        quantityReceived: parseInt(form.qty),
        condition: form.cond,
        reason: form.reason,
        photos,
        reportedBy: user.name,
        reportedAt: new Date().toISOString(),
        custodyAtIncident: dispatch.custody
      };
      save('incidents', [...data.incidents, incident]);
      sendElosEvent('INCIDENT_REPORTED', incident.id, {
        dispatchId: incident.dispatchId,
        type: incident.type,
        quantityExpected: incident.quantityExpected,
        quantityReceived: incident.quantityReceived,
        condition: incident.condition,
        reason: incident.reason,
      });
      addNotification(
        'Incident Reported',
        `${dispatch.productName}: ${form.reason || 'discrepancy detected'}`,
        'critical'
      );
    }

    const receipt = {
      id: `REC-${Date.now()}`,
      dispatchId: dispatch.id,
      quantityReceived: parseInt(form.qty),
      condition: form.cond,
      photos,
      receivedBy: user.name,
      receivedAt: new Date().toISOString(),
      hasIncident: mismatch || damaged
    };

    save('receipts', [...data.receipts, receipt]);
    sendElosEvent('RECEIPT_COMPLETED', receipt.id, {
      dispatchId: receipt.dispatchId,
      quantityReceived: receipt.quantityReceived,
      condition: receipt.condition,
      hasIncident: receipt.hasIncident,
    });
    save('dispatches', data.dispatches.map(d =>
      d.id === dispatch.id ? { ...d, status: 'completed', custody: 'receiver' } : d
    ));

    if (!mismatch && !damaged) {
      addNotification('Receipt Complete', `${dispatch.productName} received clean`, 'success');
    }

    alert(mismatch || damaged ? '⚠️ Receipt logged with incident' : '✅ Receipt complete');
    setSel(null);
    setForm({});
    setPhotos({});
  };

  const transit = data.dispatches.filter(d => d.status === 'in_transit');

  if (sel) {
    const dispatch = data.dispatches.find(d => d.id === sel);
    const needsEvidence = form.cond === 'damaged' ||
      (form.qty && parseInt(form.qty) !== dispatch.quantity);

    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Receive Delivery</h2>
        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
          <p className="font-bold text-lg">{dispatch.productName}</p>
          <p className="text-sm text-gray-600">Expected: {dispatch.quantity} units</p>
          <p className="text-sm text-gray-600">Driver: {dispatch.driver} • {dispatch.vehicle}</p>
        </div>
        <PhotoCapture label="Photo of received items *" onCapture={ph => setPhotos({...photos, received: ph})} />
        <input
          type="number"
          placeholder="Quantity received *"
          className="w-full p-3 border-2 rounded-lg"
          value={form.qty || ''}
          onChange={e => setForm({...form, qty: e.target.value})}
        />
        <select
          className="w-full p-3 border-2 rounded-lg"
          value={form.cond || ''}
          onChange={e => setForm({...form, cond: e.target.value})}
        >
          <option value="">Condition *</option>
          <option value="intact">✅ Intact</option>
          <option value="damaged">⚠️ Damaged</option>
        </select>
        {needsEvidence && (
          <>
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
              <p className="text-sm text-red-700 font-semibold">⚠️ Discrepancy — evidence required</p>
            </div>
            <PhotoCapture label="Damage evidence photo *" onCapture={ph => setPhotos({...photos, damage: ph})} />
            <select
              className="w-full p-3 border-2 rounded-lg"
              value={form.reason || ''}
              onChange={e => setForm({...form, reason: e.target.value})}
            >
              <option value="">Reason *</option>
              <option value="broken">Broken</option>
              <option value="missing">Missing / Short</option>
              <option value="wet">Wet / Soaked</option>
              <option value="other">Other</option>
            </select>
          </>
        )}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
          <p className="font-semibold text-sm mb-2">Digital Signature</p>
          <div className="bg-gray-100 h-16 rounded flex items-center justify-center">
            <p className="text-gray-500 text-sm">✓ Digitally signed by {user.name}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => { setSel(null); setForm({}); setPhotos({}); }} className="flex-1 bg-gray-200 p-3 rounded-lg font-semibold">Cancel</button>
          <button onClick={complete} className="flex-1 bg-green-600 text-white p-3 rounded-lg font-bold">Submit Receipt</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Receiver</h2>
        <TrustBadge score={user.trustScore} />
      </div>
      <h3 className="font-semibold text-gray-700">Incoming Deliveries</h3>
      {transit.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No deliveries in transit</p>
        </div>
      ) : (
        transit.map(d => (
          <div
            key={d.id}
            onClick={() => setSel(d.id)}
            className="border-2 p-4 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition-all"
          >
            <p className="font-bold">{d.productName}</p>
            <p className="text-sm text-gray-600">Expected: {d.quantity} units</p>
            <p className="text-sm text-gray-600">Driver: {d.driver}</p>
            <p className="text-xs text-green-600 mt-2 font-semibold">Tap to receive →</p>
          </div>
        ))
      )}
    </div>
  );
};

export default ReceiverView;
